/**
 * CuratedCollections — editorial research pathways across the Archive.
 * DB-curated when Admin collections exist; otherwise real code-resolved
 * defaults. Cards swipe horizontally; tapping expands the member dossiers.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { FolderOpen, ChevronDown } from "lucide-react";
import { resolveImg, SectionHeader, HRail, MiniRow } from "./homeShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CollectionCard = ({ c, open, onToggle }) => (
  <button
    onClick={onToggle}
    data-testid={`collection-card-${c.slug}`}
    aria-expanded={open}
    className="relative overflow-hidden border w-[85vw] max-w-[300px] md:w-[300px] h-[170px] md:h-[190px] flex flex-col justify-end text-left group hover:-translate-y-1 transition-transform duration-300"
    style={{ borderColor: open ? "#D92D2088" : "#2D2A26", background: "#0F0E12" }}
  >
    {c.image_url && (
      <img
        src={resolveImg(c.thumb_url || c.image_url)}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-[1.03] transition-all duration-500"
        style={{ filter: "saturate(0.6) contrast(1.05)" }}
      />
    )}
    <div aria-hidden="true" className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,10,13,0.15) 0%, rgba(11,10,13,0.94) 100%)" }} />
    <div className="relative p-4">
      <div className="flex items-center gap-2 font-mono text-[8px] uppercase tracking-[0.35em] text-[#A68F5E]">
        <FolderOpen size={10} /> Collection · {c.count} dossiers
      </div>
      <div className="mt-1.5 flex items-end justify-between gap-2">
        <h3 className="font-display text-[19px] leading-tight text-[#E4DFD3]">{c.title}</h3>
        <ChevronDown size={14} className={`shrink-0 mb-1 text-[#8A8377] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </div>
      <p className="mt-1 text-[11px] leading-snug text-[#8A8377] line-clamp-2">{c.description}</p>
    </div>
  </button>
);

const CuratedCollections = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let dead = false;
    axios.get(`${API}/home/collections`, { timeout: 15000 })
      .then((r) => { if (!dead) setItems(r.data?.items || []); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  if (!items.length) return null;
  const active = items.find((c) => c.slug === open);

  return (
    <section data-testid="home-collections" className="border-t" style={{ borderColor: "#2D2A26", background: "#08070A" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-20">
        <SectionHeader
          file="File 008 · Research Pathways"
          title="Curated Collections"
          subtitle="Editorial pathways connecting related investigations across the record."
        />
        <HRail testid="collections-rail">
          {items.map((c) => (
            <CollectionCard
              key={c.slug}
              c={c}
              open={open === c.slug}
              onToggle={() => setOpen(open === c.slug ? null : c.slug)}
            />
          ))}
        </HRail>
        {active && (
          <div
            data-testid={`collection-expansion-${active.slug}`}
            className="mt-4 border px-4 py-2 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
            style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
          >
            {active.dossiers.map((d, i) => (
              <MiniRow key={d.slug} dossier={d} index={i} testid={`collection-row-${i}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CuratedCollections;
